"use client";

import { ArrowRight, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    number: "001",
    name: "SUPERVISED PARENTING TIME",
    description: "What it is: Neutral, child-centered observation of parent-child interactions in a safe, structured environment. Who it’s for: Families navigating court-ordered requirements or reunification. Our role: Ensuring emotional and physical security while maintaining professional boundaries and compliance.",
    price: "$80/hour\n(2‑hour min)"
  }, {
    number: "002",
    name: "MONITORED EXCHANGE",
    description: "What it is: A safe, neutral transition of children between parents to eliminate direct contact. Who it’s for: High-conflict dynamics where a calm hand-off is essential for the child's peace of mind. Our role: Facilitating respectful transitions and adhering to strict safety protocols.",
    price: "$45 per exchange"
  }, {
    number: "003",
    name: "TRANSPORTATION SERVICES",
    description: "What it is: Professional, secure transport for children and vulnerable individuals. Who it’s for: Families requiring reliable door-to-door service for parenting time or essential appointments. Our role: Providing a neutral, supportive environment during every transition to ensure safety and comfort.",
    price: "$2.50\nmile"
  }, {
    number: "004",
    name: "PARENTING EDUCATION",
    description: "What it is: Structured guidance and evidence-based support for healthy family dynamics. Who it’s for: Parents seeking to develop child-focused communication and conflict-resolution skills. Our role: Providing the tools and curriculum needed to establish stable boundaries and positive interactions.",
    price: "$65/hour"
  }, {
    number: "005",
    name: "THERAPEUTIC SUPERVISION",
    description: "What it is: Specialized clinical support for families navigating complex reunification matters. Who it’s for: Families requiring a bridge between supervised contact and independent parenting. Our role: Facilitating trauma-informed sessions led by trained professionals to support family healing.",
    price: "$380/hour\n"
  }, {
    number: "006",
    name: "OBSERVATION REPORTING",
    description: "What it is: Detailed, objective, and neutral documentation of parenting time sessions. Who it’s for: Legal professionals, courts, and families requiring a factual record of interactions. Our role: Meticulously preparing reports that reflect behaviors and compliance with court orders.",
    price: "$25 per instance"
  }];
  return <section data-section-id="4606" id="specialized-family-support" className="dark bg-background text-foreground">
      <div ref={ref} className={`px-4 sm:px-6 lg:px-12 py-24 md:py-36 lg:py-[200px] transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="max-w-7xl mx-auto text-center mb-16 md:mb-24 lg:mb-32">
          <div className="font-mono text-xs sm:text-sm tracking-[0.2em] mb-8 md:mb-12 text-primary">
            / SERVICE MENU
          </div>
          <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[7rem] text-foreground">
            OUR
            <br />
            SERVICES.
          </h2>
        </div>

        {}
        <div className="max-w-7xl mx-auto border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {services.map((service, i) => <div key={service.number} data-index={i} className="group relative border-b border-border md:[&:nth-last-child(-n+2)]:border-b-0 md:[&:nth-child(odd)]:border-r transition-colors duration-300 hover:bg-muted">
                {}
                <div className="absolute left-0 top-0 bottom-0 w-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-primary" aria-hidden="true" />

                <div className="flex items-stretch p-6 md:p-8 lg:p-10 gap-6 md:gap-8">
                  {}
                  <div className="flex items-start shrink-0">
                    <span className="font-mono text-2xl md:text-3xl lg:text-4xl font-light tracking-tight text-foreground">
                      {service.number}
                    </span>
                  </div>

                  {}
                  <div className="w-px shrink-0 self-stretch bg-border" aria-hidden="true" />

                  {}
                  <div className="flex-1 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-default font-bold uppercase text-xl md:text-2xl lg:text-[2rem] leading-tight tracking-tight mb-3 md:mb-4 text-foreground">
                        {service.name}
                      </h3>
                      <p className="font-default text-base leading-relaxed max-w-md text-muted-foreground">
                        {service.description}
                      </p>
                    </div>

                    {}
                    <div className="flex flex-col items-start sm:items-end shrink-0 gap-2">
                      <span className="font-mono text-sm md:text-base font-medium tracking-wider whitespace-nowrap text-foreground">
                        {service.price}
                      </span>
                      <Link to="/ContactPage" className="font-mono text-xs tracking-[0.15em] inline-flex items-center gap-1 pb-0.5 border-b transition-all duration-300 hover:gap-2 border-primary text-foreground">
                        <Plus className="size-3" />
                        INQUIRE
                      </Link>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {}
        <div className="max-w-7xl mx-auto mt-16 md:mt-24 text-center border-t border-border pt-16">
          <p className="font-default text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto mb-8">
            Learn more about our service guidelines, intake process, and what to expect. Review our complete policies and guidelines.
          </p>
          <Link to="/PoliciesGuidelinesSafetyPage" className="inline-flex items-center justify-center px-8 py-4 bg-[#A3B18A] text-white font-default font-bold uppercase tracking-widest text-sm transition-all duration-300 hover:bg-[#5E6A52] rounded-lg shadow-md">
            View Our Policies
          </Link>
        </div>

        {}
        <div className="max-w-7xl mx-auto mt-12 text-center space-y-2">
          <p className="font-mono text-sm tracking-wider text-foreground/90">
            Initial intake: $95 per individual (non‑refundable)
          </p>
          <p className="font-mono text-sm tracking-wider text-foreground/90">
            Holiday hours: $160/hour (2‑hour minimum)
          </p>
        </div>

        {}
        <div className="max-w-7xl mx-auto mt-16 md:mt-24 text-center">
          <p className="font-default italic text-lg md:text-xl lg:text-2xl text-foreground/80">
            Need specific support?{" "}
            <Link to="/ContactPage" className="inline-flex items-center gap-2 not-italic font-default font-medium uppercase text-sm md:text-base tracking-wider border-b pb-0.5 transition-all duration-300 hover:gap-3 border-primary text-foreground">
              Contact us for a consultation
              <ArrowRight className="size-4" />
            </Link>
          </p>
        </div>
      </div>
    </section>;
}